/**
 * 환경 설정 — 고객사가 자기 화면을 어떻게 보일지 정하는 자리(브랜드·대기 화면·운영 시간·사내 주소)와,
 * 여러 서버를 한 곳에서 보게 이 서버를 다른 서버의 통합 관제에 묶는 자리.
 */
import type { ManualChapter } from '../types.js';

export const SETTINGS: ManualChapter = {
  slug: 'settings',
  title: '환경 설정',
  summary: '템플릿 머리글의 회사 이름·로고·색·사내 글꼴, 편성이 비었을 때의 화면, 재생 중 제목 표시, 새로 등록한 화면이 들 그룹, 화면이 쉬는 운영 시간, 사내 웹 페이지 허용 주소를 정하고, 서버가 여럿이면 통합 관제로 묶습니다.',
  menuPath: '/settings',
  capability: 'settings:write',
  sections: [
    {
      id: 'brand',
      title: '브랜드 — 회사 이름·로고·색·글꼴',
      blocks: [
        {
          kind: 'text',
          text: '공지·프로모션 같은 **템플릿 콘텐츠**의 머리글과 색을 회사 것으로 맞춥니다. 한 번 정하면 모든 템플릿 화면에 들어갑니다. 이미지·영상 콘텐츠는 그대로입니다.',
        },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '[[환경 설정]] 메뉴의 “브랜드” 에서 조직 이름을 넣습니다.', body: '머리글 왼쪽에 장소 이름과 함께 나옵니다. 비우면 장소만 적습니다.' },
            { title: '“머리글 장소” 에서 조직 이름 옆에 적을 글을 고릅니다.', body: '**화면 이름**(기본 — 콘솔에 등록한 이름) · **화면 위치**([[화면]] 상세의 “위치”, 비어 있는 화면은 적지 않습니다) · **직접 입력**(모든 화면에 같은 글, 예: 환경안전팀) · **표시 안 함**(조직 이름만) 가운데 하나입니다. 화면 이름이 “새 화면 (안드로이드 962×541)” 처럼 관리용이면 공공 화면에 어색하니 바꿉니다. 새 판 에이전트 화면부터 따르고, 옛 판 화면은 화면 이름을 적습니다.' },
            { title: '주 색을 고릅니다.', body: '강조 띠·배지에 쓰입니다. [[기본 색으로]] 로 되돌립니다. 긴급 주황, 증감의 초록·빨강 같은 판정 색은 바뀌지 않습니다.' },
            { title: '로고를 고릅니다.', body: '콘텐츠 목록의 이미지 중에서 고릅니다. 배경이 투명한 가로형 PNG 가 잘 어울립니다. 로고로 쓰는 이미지는 지울 수 없습니다.' },
            { title: '오른쪽 미리보기를 확인하고 [[저장]] 을 누릅니다.', body: '약 30초 안에 화면의 템플릿에 반영됩니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '사내 글꼴',
          text: '[[글꼴 올리기]] 로 회사 서체 파일(woff2·woff·ttf·otf, 20MB 까지)을 올리면, 템플릿 편집기의 글꼴에서 “사내 글꼴” 을 고를 수 있습니다. 화면은 처음 한 번 받아 둡니다. 굵기별 파일이 따로 있으면 가장 자주 쓰는 굵기(Bold) 하나를 올리세요. 올리기 전에 그 글꼴을 화면에 게시해도 되는지(배포 권한) 확인하세요.',
        },
      ],
    },
    {
      id: 'idle',
      title: '편성이 비었을 때의 화면',
      blocks: [
        {
          kind: 'text',
          text: '배정된 편성이 없거나 모든 콘텐츠가 게시 기간 밖이면 화면에 틀 것이 없습니다. 그때 무엇을 띄울지 고릅니다.',
        },
        {
          kind: 'table',
          header: ['고르는 것', '화면', '이럴 때'],
          rows: [
            ['안내 문구', '“재생할 콘텐츠가 없습니다” 처럼 까닭을 적습니다', '설치·점검 기간 — 화면이 살아 있는지 알기 쉽습니다'],
            ['검은 화면', '아무것도 띄우지 않습니다', '로비·공용 공간 — 손님에게 안내 문구가 보이지 않게'],
            ['로고', '검은 바탕 가운데 브랜드 로고', '브랜드를 지키면서 비어 있는 티를 내지 않을 때'],
          ],
        },
      ],
    },
    {
      id: 'caption',
      title: '재생 중 제목 표시',
      blocks: [
        {
          kind: 'text',
          text: '켜면 모든 화면이 **지금 틀고 있는 콘텐츠의 제목**을 화면 가장자리에 띄웁니다. 제목은 콘텐츠 목록의 제목이고, 바꾸면 화면에도 곧 바뀝니다. 대기 화면·안내 화면에는 띄우지 않습니다.',
        },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '[[환경 설정]] 의 “재생 중 제목 표시” 에서 “재생 중인 콘텐츠의 제목을 화면에 띄우기” 를 켭니다.' },
            { title: '미리보기 화면에서 자리를 누릅니다.', body: '위·아래마다 왼쪽·가운데·오른쪽 여섯 곳 중 하나입니다. 콘텐츠의 중요한 글자를 가리지 않는 자리를 고릅니다.' },
            { title: '긴 제목을 다루는 법을 고르고 [[저장]] 을 누릅니다.', body: '약 30초 안에 화면에 나옵니다.' },
          ],
        },
        {
          kind: 'table',
          header: ['긴 제목', '화면', '이럴 때'],
          rows: [
            ['말줄임', '한 줄로 두고 넘치는 끝을 … 로 줄입니다', '제목이 대개 짧고, 화면이 차분해야 할 때'],
            ['흐르기', '한 줄로 두고 넘치면 글자가 옆으로 흘러 끝까지 보입니다 — 들어가는 제목은 흐르지 않습니다', '긴 제목도 끝까지 읽혀야 할 때'],
            ['두 줄', '두 줄까지 꺾고, 그래도 넘치면 끝을 … 로 줄입니다', '움직이지 않으면서 더 많이 보여야 할 때'],
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '에이전트 1.0.2 부터',
          text: '그 전 버전의 화면은 이 설정을 모르고 지나칩니다. 화면의 에이전트가 1.0.2 이상으로 올라가면 나옵니다.',
        },
      ],
    },
    {
      id: 'new-screen-group',
      title: '새 화면 그룹 — 등록하자마자 틀 편성',
      blocks: [
        {
          kind: 'text',
          text: '화면을 등록할 때 그룹을 고르지 않으면 **“새 화면 그룹”** 에 듭니다. 그 그룹에 배정한 플레이리스트·스케줄을 곧바로 틀기 때문에, 화면을 붙이자마자 무엇이 나올지 정해 둘 수 있습니다. “그룹 없이” 로 두면 새 화면은 그룹 없이 들어 대기 화면을 띄웁니다.',
        },
        {
          kind: 'text',
          text: '납품받은 서버에는 **「기본 콘텐츠」** 그룹이 정해져 있고, 같은 이름의 플레이리스트(설치 때 함께 들어온 콘텐츠)를 틉니다. 내 편성으로 바꾸려면 화면을 다른 그룹으로 옮기거나 화면에 직접 배정합니다 — 그쪽이 이깁니다. [[화면 그룹]] 메뉴에서 「기본 콘텐츠」 그룹의 배정을 바꾸면 그 그룹의 화면이 모두 바뀝니다.',
        },
      ],
    },
    {
      id: 'hours',
      title: '운영 시간 — 화면이 쉬는 시간',
      blocks: [
        {
          kind: 'text',
          text: '운영 시간을 정하면 **그 시간 밖에는 편성·캠페인이 있어도 화면이 검게 쉽니다.** 영업이 끝난 밤에 화면이 광고를 틀지 않게 하고, 그 시간의 재생은 리포트에도 잡히지 않습니다.',
        },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '“운영 시간 정하기” 를 켭니다.' },
            { title: '요일을 고르고 여는 시각·닫는 시각을 넣습니다.', body: '시각 칸을 누르면 아래에 **시·분 목록**이 열립니다 — 시를 고른 뒤 분을 고르면 들어가고 목록이 닫힙니다(분은 5분 간격). 숫자로 적어도 됩니다: “1930” 이나 “19:30” 은 19:30, “7” 은 07:00 입니다. 시각은 24시간으로 적힙니다. 닫는 시각이 여는 시각보다 이르면 다음 날까지입니다(예: 18:00~02:00 은 새벽 2시까지). 자정까지면 닫는 시각을 00:00 으로 둡니다 — “자정까지” 표시가 붙습니다.' },
            { title: '요일마다 다르면 [[시간대 추가]] 로 줄을 더합니다.' },
            { title: '[[저장]] 을 누릅니다.', body: '쉬는 시간이 되면 약 30초 안에 화면이 검게 되고, 여는 시각이 되면 다시 켜집니다. 그 사이 서버와 연락이 끊겨도 화면은 미리 받은 시간표대로 켜지고 꺼집니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '쉬는 시간에도 나가는 것',
          text: '**긴급 송출**과 **바로 보내기**는 운영 시간과 상관없이 나갑니다 — 급한 안내는 밤에도 띄워야 하기 때문입니다.',
        },
        {
          kind: 'callout',
          tone: 'tip',
          title: '사옥마다 시간이 다르면',
          text: '여기서 정한 것은 모든 화면의 기본입니다. 24시간 도는 로비처럼 다른 화면은 화면 상세의 [[화면 설정]] 에서 “늘 켜기” 나 “이 화면만” 으로 따로 정합니다. 쉬는 중인 화면은 모니터링과 화면 상세의 화면 보기에 “운영 시간 외 — 쉬는 중” 으로 보입니다.',
        },
      ],
    },
    {
      id: 'allowlist',
      title: '사내 웹 페이지를 화면에 띄우기',
      blocks: [
        {
          kind: 'text',
          text: '웹 콘텐츠는 기본으로 **공개 인터넷 주소만** 받습니다. 사설 IP(10.x·192.168.x 등)·사내 도메인(.local·.intranet 등)·점 없는 이름은 막혀 있습니다 — 사내 관리 페이지가 실수로 로비 화면에 뜨지 않게 하려는 것입니다. 사내 포털·사내 대시보드를 띄우려면 그 주소를 “웹 콘텐츠 허용 주소” 에 적습니다 — 이 패널은 접혀 있으니 제목을 눌러 펼칩니다(머리에 적어 둔 주소 수가 보입니다).',
        },
        {
          kind: 'table',
          header: ['적는 모양', '뜻'],
          rows: [
            ['portal.example.co.kr', '그 호스트 하나. 점 없는 사내 이름(intranet)도 됩니다.'],
            ['*.intra.example.co.kr', '그 아래 이름 전부'],
            ['10.20.30.40 · 10.20.0.0/16', 'IP 하나 또는 대역(/8 이상)'],
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '목록에서 지워도 이미 등록한 웹 콘텐츠는 그대로 나갑니다',
          text: '내리려면 그 콘텐츠를 보관하세요. 주소를 바꾼 기록은 변경 기록에 남습니다. 화면 기기가 그 사내 주소에 닿을 수 있어야 실제로 보입니다(같은 사내망).',
        },
      ],
    },
    {
      id: 'html-offline',
      title: '올린 HTML 의 바깥 연결 막기',
      blocks: [
        {
          kind: 'text',
          text: '올린 HTML 이 CDN 같은 바깥 주소에서 글꼴·그림·스크립트를 불러오는 부분은 인터넷이 있어야 나오고, 그 주소의 파일이 바뀌면 콘솔에서 승인한 뒤에도 화면이 바뀝니다. “올린 HTML 의 바깥 연결” 에서 **바깥 연결 막기** 를 켜고 [[저장]] 하면 화면과 콘솔 미리보기가 HTML 을 그 파일 안의 것(글·그림·스크립트)만으로 띄웁니다 — 인터넷이 없는 현장은 잃는 것이 없습니다. 바깥에서 불러오는 HTML 은 콘텐츠 상세에 그 주소가 나오고, 막아 두면 “막아 두어 빠집니다” 로 알려 줍니다. 반반 화면·구역 나누기의 HTML 칸도 같습니다.',
        },
        {
          kind: 'callout',
          tone: 'note',
          title: '새 판 에이전트부터 따릅니다',
          text: '옛 판 화면은 예전처럼 바깥 주소를 엽니다. 화면은 마지막으로 받은 값을 저장해 서버 없이 켜져도 같은 규칙으로 틉니다.',
        },
      ],
    },
    {
      id: 'hub-link',
      title: '여러 서버를 한 곳에서 — 통합 관제 연결',
      blocks: [
        {
          kind: 'text',
          text: '현장마다 서버를 따로 둔 조직은 한 서버의 **통합 관제**에서 다른 서버들의 화면을 모아 보고 원격 명령을 보냅니다. 이 패널은 **모이는 쪽**의 일 — 이 서버를 다른 서버의 통합 관제에 묶을 토큰을 만듭니다. 토큰을 넣는 쪽(모아 보는 서버)의 일과 연결 상태를 읽는 법은 통합 관제 장에 있습니다.',
        },
        {
          kind: 'steps',
          title: '따라 하기',
          steps: [
            { title: '“통합 관제 연결” 에서 [[통합 관제 토큰 만들기]] 를 누릅니다.', body: '나온 토큰(sk_…)을 복사합니다. **창을 떠나면 다시 볼 수 없습니다.** 패널 아래에 이 서버의 주소가 적혀 있습니다 — 망이 다른 서버는 Sentinel Grid 주소를 씁니다(설치한 곳에 물어보세요).' },
            { title: '모아 보는 서버의 관리자에게 토큰과 주소를 건넵니다.', body: '그쪽 [[통합 관제]] 의 [[서버 추가]] 에 넣으면 묶입니다. 묶이면 이 패널의 토큰 줄에 **마지막 사용**이 몇 분 안쪽으로 적힙니다 — 통합 관제가 30초마다 부릅니다.' },
          ],
        },
        {
          kind: 'callout',
          tone: 'warn',
          title: '토큰은 이 서버의 화면을 끌 수 있는 열쇠입니다',
          text: '복사한 토큰은 곧바로 통합 관제에 넣고 다른 곳에 적어 두지 마세요. 잃었거나 새어 나갔으면 [[폐기]] 하고 새로 만듭니다 — 폐기하면 그 토큰으로 모아 보던 통합 관제는 곧바로 연결을 잃습니다(“토큰 거절”). 그때는 모아 보는 서버에서 서버 이름을 눌러 새 토큰을 넣습니다.',
        },
      ],
    },
  ],
};
